import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertCircle, TrendingUp, Package, ShoppingCart } from "lucide-react";

export function LeadLimitModal({ isOpen, onClose, limitDetails }) {
  const {
    current_leads = 0,
    max_leads = 0,
    available = 0,
    leads_to_add = 1,
    plan = 'base',
    topup_available = true,
    topup_price = 19,
    topup_size = 100
  } = limitDetails || {};

  const planTopupPrices = {
    base: 19,
    tier1: 19,
    tier2: 20
  };

  const actualTopupPrice = planTopupPrices[plan] || topup_price;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
              <Package className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <DialogTitle className="text-xl">Lead Limit Reached</DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                {plan.charAt(0).toUpperCase() + plan.slice(1)} Plan
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-4">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-orange-800">
                <p className="font-medium mb-1">You've reached your lead limit</p>
                <p className="text-orange-700">
                  Your current plan includes <strong>{max_leads} leads</strong>, 
                  and you currently have <strong>{current_leads} leads</strong>.
                  {leads_to_add > 1 && (
                    <span> You're trying to add <strong>{leads_to_add} new leads</strong>.</span>
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Package className="w-4 h-4 text-gray-600" />
                Current Status
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Current Leads:</span>
                  <span className="font-medium text-gray-900">{current_leads} / {max_leads}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Available:</span>
                  <span className="font-medium text-gray-900">{available} leads</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Plan:</span>
                  <span className="font-medium text-gray-900 capitalize">{plan}</span>
                </div>
              </div>
            </div>

            {topup_available && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4 text-green-600" />
                  Quick Solution: Buy Lead Top-Up
                </h4>
                <div className="bg-white rounded-md p-3 mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-semibold text-gray-900">Lead Top-Up Package</p>
                      <p className="text-sm text-gray-600">{topup_size} additional leads</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-green-600">€{actualTopupPrice}</p>
                      <p className="text-xs text-gray-500">one-time</p>
                    </div>
                  </div>
                  <Button 
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={() => {
                      // TODO: Implement payment flow
                      console.log('Purchase lead top-up');
                      onClose();
                    }}
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Purchase {topup_size} More Leads
                  </Button>
                </div>
                <p className="text-xs text-green-700">
                  * After purchase, your new limit will be {max_leads + topup_size} leads
                </p>
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-blue-600" />
                Or Upgrade Your Plan
              </h4>
              <p className="text-sm text-blue-800 mb-3">
                Get more leads included every month with a higher tier plan.
              </p>
              <Button
                variant="outline"
                className="w-full border-blue-300 text-blue-700 hover:bg-blue-100"
                onClick={() => {
                  // TODO: Navigate to pricing/upgrade page
                  console.log('Navigate to upgrade page');
                  onClose();
                }}
              >
                View Plans & Pricing
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
